import React, { useState } from 'react'
import { Component, LocaleCode } from '../types/component'
import { getLocalizedText } from '../utils/localization'

interface ContactFormProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

export function ContactForm({
  component,
  locale,
  apiUrl,
  style,
}: ContactFormProps) {
  const config = component.config as any
  const title = getLocalizedText(config.title, locale) || 'Get in Touch'
  const subtitle =
    getLocalizedText(config.subtitle, locale) ||
    "Fill out the form below and we'll get back to you soon."
  const submitButtonText =
    getLocalizedText(config.submitButtonText, locale) || 'Send Message'
  const showNameField = config.showNameField !== false
  const showEmailField = config.showEmailField !== false
  const showPhoneField = config.showPhoneField !== false
  const showSubjectField = config.showSubjectField !== false
  const showMessageField = config.showMessageField !== false
  const layout = config.layout || 'stacked'

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })

  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch(`${apiUrl}/api/v1/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error('Failed to submit form')
      }

      setSubmitted(true)
      setTimeout(() => {
        setSubmitted(false)
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' })
      }, 3000)
    } catch (err) {
      console.error('Form submission error:', err)
      setError('Failed to submit form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const containerStyle: React.CSSProperties = {
    ...style,
    padding: '24px',
    backgroundColor: style.backgroundColor || '#ffffff',
  }

  const maxWidthStyle: React.CSSProperties = {
    maxWidth: '672px',
    margin: '0 auto',
  }

  const formStyle: React.CSSProperties = {
    display: layout === 'two-column' ? 'grid' : 'flex',
    flexDirection: layout === 'two-column' ? undefined : 'column',
    gridTemplateColumns: layout === 'two-column' ? 'repeat(2, 1fr)' : undefined,
    gap: '16px',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px',
  }

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.2s',
    opacity: isSubmitting ? 0.7 : 1,
  }

  return (
    <div style={containerStyle}>
      <div style={maxWidthStyle}>
        {title && (
          <h2
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#111827',
              marginBottom: '8px',
            }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>{subtitle}</p>
        )}

        {submitted ? (
          <div
            style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
            }}
          >
            <div style={{ color: '#16a34a', fontSize: '36px', marginBottom: '8px' }}>
              ✓
            </div>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#166534',
                marginBottom: '4px',
              }}
            >
              Message Sent!
            </h3>
            <p style={{ color: '#15803d' }}>
              Thank you for contacting us. We'll get back to you soon.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={formStyle}>
              {showNameField && (
                <div
                  style={{
                    gridColumn: layout === 'two-column' ? 'span 1' : undefined,
                  }}
                >
                  <label style={labelStyle}>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder="Your name"
                    onFocus={(e) =>
                      (e.target.style.boxShadow = '0 0 0 2px #3b82f6')
                    }
                    onBlur={(e) => (e.target.style.boxShadow = 'none')}
                  />
                </div>
              )}

              {showEmailField && (
                <div
                  style={{
                    gridColumn: layout === 'two-column' ? 'span 1' : undefined,
                  }}
                >
                  <label style={labelStyle}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={inputStyle}
                    placeholder="your@email.com"
                    onFocus={(e) =>
                      (e.target.style.boxShadow = '0 0 0 2px #3b82f6')
                    }
                    onBlur={(e) => (e.target.style.boxShadow = 'none')}
                  />
                </div>
              )}

              {showPhoneField && (
                <div
                  style={{
                    gridColumn: layout === 'two-column' ? 'span 1' : undefined,
                  }}
                >
                  <label style={labelStyle}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder="(555) 123-4567"
                    onFocus={(e) =>
                      (e.target.style.boxShadow = '0 0 0 2px #3b82f6')
                    }
                    onBlur={(e) => (e.target.style.boxShadow = 'none')}
                  />
                </div>
              )}

              {showSubjectField && (
                <div
                  style={{
                    gridColumn: layout === 'two-column' ? 'span 2' : undefined,
                  }}
                >
                  <label style={labelStyle}>Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder="How can we help?"
                    onFocus={(e) =>
                      (e.target.style.boxShadow = '0 0 0 2px #3b82f6')
                    }
                    onBlur={(e) => (e.target.style.boxShadow = 'none')}
                  />
                </div>
              )}

              {showMessageField && (
                <div
                  style={{
                    gridColumn: layout === 'two-column' ? 'span 2' : undefined,
                  }}
                >
                  <label style={labelStyle}>Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    style={{
                      ...inputStyle,
                      resize: 'none',
                    }}
                    placeholder="Tell us more about your inquiry..."
                    onFocus={(e) =>
                      (e.target.style.boxShadow = '0 0 0 2px #3b82f6')
                    }
                    onBlur={(e) => (e.target.style.boxShadow = 'none')}
                  />
                </div>
              )}
            </div>

            {error && (
              <p style={{ color: '#ef4444', marginTop: '8px', fontSize: '14px' }}>
                {error}
              </p>
            )}

            <div style={{ marginTop: '24px' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={buttonStyle}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.opacity = '0.9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.opacity = '1'
                  }
                }}
              >
                {isSubmitting ? 'Sending...' : submitButtonText}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
